import { useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n";
import { useAuth } from "@/features/auth/AuthContext";
import { api } from "@/lib/api";
import { supabase } from "@/lib/supabase";
import { CONTENT_TYPES } from "@/lib/constants";
import type { CanvasBackground } from "@/lib/database.types";
import { useJobPoller } from "./useJobPoller";
import { runClientCanvas } from "./clientCanvas";

type Lang = "ar" | "en";

export function CreatePage() {
  const { t } = useI18n();
  const { profile, session, hasActiveSub, refresh } = useAuth();
  const job = useJobPoller();

  const [topic, setTopic] = useState("");
  const [language, setLanguage] = useState<Lang>("ar");
  const [cohortIdx, setCohortIdx] = useState<number[]>([]);
  const [caption, setCaption] = useState("");
  const [headline, setHeadline] = useState("");
  const [toggles, setToggles] = useState({ doctorImage: false, phone: false, logo: false });
  const [backgrounds, setBackgrounds] = useState<CanvasBackground[]>([]);
  const [selectedBgUrl, setSelectedBgUrl] = useState<string | null>(null); // null = default white→grey
  const [pieceId, setPieceId] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editInstruction, setEditInstruction] = useState("");
  // Free Canvas runs entirely client-side (no job poller).
  const [clientBusy, setClientBusy] = useState(false);
  const [clientUrl, setClientUrl] = useState<string | null>(null);
  // AI path: the server returns a background; we composite it in the browser
  // once the job completes. The ref avoids re-compositing on every poll tick.
  const awaitingAiBg = useRef(false);

  const cohorts = profile?.target_cohorts ?? [];
  const captionLocked = !!pieceId; // title/desc lock after generation (PRD §6.7)

  // Load the user's canvas-background library (for the Canvas-path picker).
  useEffect(() => {
    const uid = session?.user?.id;
    if (!uid) return;
    void supabase
      .from("canvas_backgrounds")
      .select("*")
      .eq("user_id", uid)
      .order("created_at", { ascending: false })
      .then(({ data }) => setBackgrounds((data as CanvasBackground[]) ?? []));
  }, [session?.user?.id]);

  async function genCaption() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.generateCopy({ topic, language, cohortIdx });
      setCaption(r.caption);
      setHeadline(r.headline);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("error"));
    } finally {
      setBusy(false);
    }
  }

  async function generate(path: "free" | "ai") {
    setError(null);
    if (path === "free") return generateFree();

    setBusy(true);
    try {
      const r = await api.generateDesign({
        topic,
        language,
        headline,
        caption,
        cohortIdx,
        toggles,
        pieceId,
      });
      setPieceId(r.pieceId);
      setClientUrl(null);
      awaitingAiBg.current = true; // composite the background when the job is done
      job.track(r.jobId);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("error"));
    } finally {
      setBusy(false);
    }
  }

  // AI path step 2: the job finished and returned an AI background URL — now
  // composite the brand layer over it in the browser and finalize the piece.
  async function compositeAiBackground(backgroundUrl: string) {
    if (!session?.user || !profile) return;
    setClientBusy(true);
    job.reset();
    try {
      const r = await runClientCanvas({
        userId: session.user.id,
        profile,
        language,
        topic,
        caption,
        headline,
        toggles,
        existingPieceId: pieceId,
        backgroundUrl,
        mode: "ai",
      });
      setPieceId(r.pieceId);
      setClientUrl(r.url);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("error"));
    } finally {
      setClientBusy(false);
    }
  }

  // When an AI job completes, hand its background off to the browser compositor.
  useEffect(() => {
    if (!awaitingAiBg.current) return;
    if (job.status === "done" && job.resultUrl) {
      awaitingAiBg.current = false;
      void compositeAiBackground(job.resultUrl);
    } else if (job.status === "failed") {
      awaitingAiBg.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job.status, job.resultUrl]);

  // Free Canvas: composite + upload in the browser (no AI, no credits, no job).
  async function generateFree() {
    if (!session?.user || !profile) return;
    setClientBusy(true);
    setClientUrl(null);
    job.reset();
    try {
      const r = await runClientCanvas({
        userId: session.user.id,
        profile,
        language,
        topic,
        caption,
        headline,
        toggles,
        existingPieceId: pieceId,
        mode: "canvas",
        backgroundUrl: selectedBgUrl,
      });
      setPieceId(r.pieceId);
      setClientUrl(r.url);
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("error"));
    } finally {
      setClientBusy(false);
    }
  }

  async function requestEdit() {
    if (!pieceId || !editInstruction) return;
    setBusy(true);
    try {
      const r = await api.editDesign({ pieceId, editInstruction });
      awaitingAiBg.current = true; // composite the edited background when done
      job.track(r.jobId);
      setEditInstruction("");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : t("error"));
    } finally {
      setBusy(false);
    }
  }

  function startOver() {
    job.reset();
    setClientUrl(null);
    setPieceId(undefined);
    setCaption("");
    setHeadline("");
    setTopic("");
  }

  // Unfreeze: stop polling a stuck job and clear the in-flight UI. The piece is
  // kept so the user can retry without re-entering the topic/headline.
  function cancelGeneration() {
    job.reset();
    setClientBusy(false);
    setBusy(false);
    setError(null);
  }

  // Unify the server-job and client-canvas result states for rendering.
  const pending = clientBusy || job.status === "pending";
  const resultUrl = clientUrl ?? (job.status === "done" ? job.resultUrl : null);

  return (
    <div>
      <h1 className="mb-4 text-xl font-bold">{t("createTitle")}</h1>

      {/* Content type selector (only Main built this pass) */}
      <div className="mb-4 flex gap-2 overflow-x-auto pb-1">
        {CONTENT_TYPES.map((ct) => (
          <button
            key={ct.key}
            disabled={!ct.built}
            className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm ${
              ct.key === "main"
                ? "bg-accent text-accent-fg"
                : "bg-slate-100 text-muted disabled:opacity-50"
            }`}
          >
            {t("createTitle") && ct.labelAr}
          </button>
        ))}
      </div>

      {!hasActiveSub && (
        <div className="card mb-4 border border-amber-200 bg-amber-50 text-center text-sm text-amber-800">
          {t("subscribeToCreate")}
        </div>
      )}

      {/* Shared controls */}
      <section className="card mb-4 space-y-3">
        <div>
          <label className="label">{t("topic")}</label>
          <textarea
            className="input min-h-16"
            value={topic}
            disabled={captionLocked}
            onChange={(e) => setTopic(e.target.value)}
          />
        </div>

        <div className="flex gap-2">
          <button
            className={language === "ar" ? "btn-primary flex-1" : "btn-ghost flex-1"}
            onClick={() => setLanguage("ar")}
          >
            {t("arabic")}
          </button>
          <button
            className={language === "en" ? "btn-primary flex-1" : "btn-ghost flex-1"}
            onClick={() => setLanguage("en")}
          >
            {t("english")}
          </button>
        </div>

        {cohorts.length > 0 && (
          <div>
            <label className="label">{t("targetCohort")}</label>
            <div className="flex flex-wrap gap-2">
              {cohorts.map((c, i) => {
                const on = cohortIdx.includes(i);
                return (
                  <button
                    key={i}
                    className={`rounded-full px-3 py-1 text-xs ${on ? "bg-accent text-accent-fg" : "bg-slate-100 text-muted"}`}
                    onClick={() =>
                      setCohortIdx(on ? cohortIdx.filter((x) => x !== i) : [...cohortIdx, i])
                    }
                  >
                    {c.gender} {c.age_from}-{c.age_to}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Toggles */}
        <div className="flex flex-wrap gap-2 pt-1">
          {(["doctorImage", "phone", "logo"] as const).map((k) => (
            <label key={k} className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={toggles[k]}
                onChange={(e) => setToggles({ ...toggles, [k]: e.target.checked })}
              />
              {k === "doctorImage" ? t("addDoctorImage") : k === "phone" ? t("addPhone") : t("addLogo")}
            </label>
          ))}
        </div>

        <button
          className="btn-primary w-full"
          disabled={!hasActiveSub || busy || !topic}
          onClick={genCaption}
        >
          {busy ? t("generating") : `${t("generateCaption")} (1)`}
        </button>
      </section>

      {/* Caption + headline (editable) */}
      {(caption || headline) && (
        <section className="card mb-4 space-y-3">
          <div>
            <label className="label">{t("topic")}</label>
            <textarea
              className="input min-h-24"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
            />
          </div>
          <div>
            <label className="label">{t("headline")}</label>
            <input
              className="input"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
            />
          </div>

          {/* Canvas background picker (default white→grey + the user's library). */}
          <div>
            <label className="label">{t("selectBackground")}</label>
            <div className="flex flex-wrap gap-2">
              <button
                className={`flex h-14 w-14 items-center justify-center rounded-lg text-[10px] ring-2 ${selectedBgUrl === null ? "ring-accent" : "ring-slate-200"}`}
                style={{ background: "linear-gradient(135deg, #ffffff, #e5e7eb)" }}
                onClick={() => setSelectedBgUrl(null)}
              >
                {t("defaultBackground")}
              </button>
              {backgrounds.map((b) => (
                <button
                  key={b.id}
                  className={`h-14 w-14 overflow-hidden rounded-lg ring-2 ${selectedBgUrl === b.image_url ? "ring-accent" : "ring-slate-200"}`}
                  onClick={() => setSelectedBgUrl(b.image_url)}
                >
                  <img src={b.image_url} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              className="btn-ghost flex-1"
              disabled={!hasActiveSub || busy || clientBusy || !headline || pending}
              onClick={() => generate("free")}
            >
              {t("freeCanvas")}
            </button>
            <button
              className="btn-primary flex-1"
              disabled={!hasActiveSub || busy || clientBusy || !headline || pending}
              onClick={() => generate("ai")}
            >
              {t("aiDesign")} {t("credits2")}
            </button>
          </div>
        </section>
      )}

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      {/* Job progress / result */}
      {pending && (
        <section className="card mb-4">
          <div className="skeleton mb-3 h-64 w-full" />
          <p className="mb-3 text-center text-sm text-muted">
            {clientBusy ? t("generating") : job.stage ?? t("generating")}
          </p>
          <button className="btn-ghost w-full" onClick={cancelGeneration}>
            {t("cancel")}
          </button>
        </section>
      )}

      {!pending && job.status === "failed" && !resultUrl && (
        <section className="card mb-4 text-center">
          <p className="mb-3 text-sm text-red-600">{job.error ?? t("error")}</p>
          <button className="btn-primary" onClick={() => generate("free")}>
            {t("retryFree")}
          </button>
        </section>
      )}

      {!pending && resultUrl && (
        <section className="card mb-4">
          <img src={resultUrl} alt="" className="mb-3 w-full rounded-xl" />
          <div className="grid gap-2">
            <a className="btn-primary" href={resultUrl} download target="_blank" rel="noopener">
              {t("download")}
            </a>
            <div className="flex gap-2">
              <input
                className="input"
                placeholder={t("requestEdit")}
                value={editInstruction}
                onChange={(e) => setEditInstruction(e.target.value)}
              />
              <button
                className="btn-ghost px-4"
                disabled={busy || !editInstruction}
                onClick={requestEdit}
              >
                {t("requestEdit")} (2)
              </button>
            </div>
            <button className="btn-ghost" onClick={startOver}>
              + {t("createTitle")}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
